import { useEffect, useMemo, useState } from 'react';
import { capitalize } from '@mui/material';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ListPage, ListRow } from '/src/components';
import { useCompanyStore, useUserStore } from '/src/context';
import { Note } from '/src/lib/models';
import { DataModelType } from '/src/lib/types';
import { currency, isNumber } from '/src/utils';

/**
 * List Companies Page
 */
export const ListCompaniesPage = observer(() => {
  const defaultSortType = 'name';
  const defaultSortReverse = false;

  /** Context **/
  const companyStore = useCompanyStore();
  const userStore = useUserStore();

  /** State **/
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/
  const companies = companyStore.companies;

  const listData = useMemo(() => {
    if (companies) {
      const list = companies.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    }
  }, [companies, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /** Effects **/

  useEffect(() => {
    companyStore.query();
  }, []);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Company}
      headerItems={[
        { flex: 5, propName: 'notes', label: 'Notes' },
        { flex: 1, propName: 'name', label: 'Name' },
        { flex: 1, propName: 'currentCredit', label: 'Current Available Credit' },
        { flex: 1, propName: 'principalBalance', label: 'Current Balance' },
        { flex: 1, propName: 'currentAssetValue', label: 'Current Asset Values' },
        {
          flex: 1,
          propName: 'revenue',
          label: userStore.isAdmin ? 'Revenue (YTD)' : userStore.isLender ? 'Revenue (YTD)' : 'Costs (YTD)',
        },
        { flex: 1, propName: 'socketLineOfBusiness', label: 'Line of Business' },
        { flex: 1, propName: 'status', label: 'Status' },
      ]}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      notes={notes}
      notesName={notesName}
      setNotes={setNotes}
      setNotesName={setNotesName}
    >
      {listData?.map((company, i) => (
        <ListRow
          key={company._id}
          items={[
            {
              propName: 'notes',
              isNotes: true,
              flex: 5,
              hide: !company.notes?.length,
              showNotesPopup: () => showNotesPopup(company.notes, company.name),
            },
            { propName: 'name', content: company.name, url: `/companies/${company._id}`, hideOverflow: true },
            { propName: 'currentCredit', content: currency.format(company.currentCredit) },
            { propName: 'principalBalance', content: currency.format(company.principalBalance) },
            { propName: 'currentAssetValue', content: currency.format(company.currentAssetValue) },
            {
              propName: 'revenue',
              content: isNumber(company.revenue) ? currency.format(company.revenue) : '',
            },
            { propName: 'socketLineOfBusiness', content: company.socketLineOfBusiness },
            { propName: 'status', content: capitalize(company.status) },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
